/**
 * Pure utility functions for TraceExplorer.
 * No dependencies on reactive state or the DOM.
 */

/**
 * Computes depth for a single span (legacy, O(depth × n) per call).
 * Use computeSpanDepths() instead for better performance when computing many depths.
 */
export function getSpanDepth(span, allSpans) {
  let depth = 0;
  let current = span;
  while (current.parent_span_id) {
    depth++;
    current = allSpans.find((s) => s.span_id === current.parent_span_id);
    if (!current) break;
  }
  return depth;
}

/**
 * Pre-computes depths for all spans in O(n) time.
 * Returns a Map<span_id, depth> for efficient lookup.
 */
export function computeSpanDepths(allSpans) {
  const byId = new Map(allSpans.map((s) => [s.span_id, s]));
  const depths = new Map();
  const visiting = new Set();

  function getDepth(span) {
    if (depths.has(span.span_id)) return depths.get(span.span_id);
    if (visiting.has(span.span_id)) {
      depths.set(span.span_id, 0);
      return 0;
    }
    if (!span.parent_span_id || !byId.has(span.parent_span_id)) {
      depths.set(span.span_id, 0);
      return 0;
    }
    visiting.add(span.span_id);
    const d = 1 + getDepth(byId.get(span.parent_span_id));
    depths.set(span.span_id, d);
    visiting.delete(span.span_id);
    return d;
  }

  for (const s of allSpans) getDepth(s);
  return depths;
}

export function computeTimeRange(spanList) {
  if (!spanList.length) return { minNs: 0, maxNs: 0, durationNs: 1 };
  let minNs = Infinity,
    maxNs = -Infinity;
  for (const s of spanList) {
    if (s.start_time_ns < minNs) minNs = s.start_time_ns;
    if (s.end_time_ns > maxNs) maxNs = s.end_time_ns;
  }
  return { minNs, maxNs, durationNs: Math.max(maxNs - minNs, 1) };
}

export function assignLanes(spanList) {
  const sorted = [...spanList].sort((a, b) => a.start_time_ns - b.start_time_ns);
  const ends = [];
  const result = [];
  for (const span of sorted) {
    let lane = ends.findIndex((e) => e <= span.start_time_ns);
    if (lane === -1) lane = ends.length;
    ends[lane] = span.end_time_ns;
    result.push({ ...span, lane });
  }
  return { spans: result, laneCount: Math.max(ends.length, 1) };
}

export function computeProcessRows(spanList) {
  const map = new Map();
  for (const span of spanList) {
    const pid = span.attributes?.["process.pid"] || "unknown";
    const label =
      span.attributes?.["process.label"] || span.attributes?.["process.registered_name"] || pid;
    if (!map.has(pid)) map.set(pid, { pid, label, parentPid: null, rawSpans: [] });
    const entry = map.get(pid);
    entry.rawSpans.push(span);
    if (!entry.parentPid && span.attributes?.["process.parent_pid"]) {
      entry.parentPid = span.attributes["process.parent_pid"];
    }
  }
  return [...map.values()]
    .map(({ pid, label, parentPid, rawSpans }) => {
      const { spans: laned, laneCount } = assignLanes(rawSpans);
      // Use reduce to avoid stack overflow with large arrays
      const firstStart = rawSpans.reduce(
        (min, s) => (s.start_time_ns < min ? s.start_time_ns : min),
        Infinity,
      );
      return {
        pid,
        label,
        parentPid,
        spans: laned,
        laneCount,
        spanCount: rawSpans.length,
        firstStart,
      };
    })
    .sort((a, b) => a.firstStart - b.firstStart);
}

export function buildProcessTree(rows) {
  const nodeMap = new Map();
  for (const row of rows) {
    nodeMap.set(row.pid, { row, children: [] });
  }

  const roots = [];
  for (const [pid, node] of nodeMap) {
    const parentPid = node.row.parentPid;
    if (parentPid && nodeMap.has(parentPid) && parentPid !== pid) {
      nodeMap.get(parentPid).children.push(node);
    } else {
      roots.push(node);
    }
  }

  const result = [];
  const visited = new Set();

  function visit(node, depth, isLastChild) {
    if (visited.has(node.row.pid)) return;
    visited.add(node.row.pid);
    const children = node.children.sort((a, b) => a.row.firstStart - b.row.firstStart);
    result.push({
      ...node.row,
      depth,
      isLastChild,
      hasChildren: children.length > 0,
    });
    children.forEach((child, i) => visit(child, depth + 1, i === children.length - 1));
  }

  roots.sort((a, b) => a.row.firstStart - b.row.firstStart);
  roots.forEach((root, i) => visit(root, 0, i === roots.length - 1));

  for (const [pid, node] of nodeMap) {
    if (!visited.has(pid)) {
      result.push({ ...node.row, depth: 0, isLastChild: true, hasChildren: false });
    }
  }

  return result;
}

export function filterCollapsedChildren(rows, collapsedPids) {
  if (!collapsedPids.size) return rows;
  const result = [];
  let skipBelowDepth = Infinity;
  for (const row of rows) {
    if (row.depth > skipBelowDepth) continue;
    skipBelowDepth = Infinity;
    result.push(row);
    if (collapsedPids.has(row.pid)) {
      skipBelowDepth = row.depth;
    }
  }
  return result;
}

export function computeVisibleRows(rows, sort, search, hidden) {
  let result = rows.filter((r) => !hidden.has(r.pid));
  if (sort === "tree") {
    if (search.trim()) {
      const q = search.toLowerCase();
      const matched = new Set();
      for (const r of result) {
        if (r.label.toLowerCase().includes(q) || r.pid.toLowerCase().includes(q)) {
          matched.add(r.pid);
          // Include ancestors so tree structure stays coherent
          let cur = r;
          while (cur.parentPid) {
            matched.add(cur.parentPid);
            cur = result.find((rr) => rr.pid === cur.parentPid) || { parentPid: null };
          }
        }
      }
      result = result.filter((r) => matched.has(r.pid));
    }
    return buildProcessTree(result);
  }
  if (search.trim()) {
    const q = search.toLowerCase();
    result = result.filter(
      (r) => r.label.toLowerCase().includes(q) || r.pid.toLowerCase().includes(q),
    );
  }
  if (sort === "name") return [...result].sort((a, b) => a.label.localeCompare(b.label));
  if (sort === "count") return [...result].sort((a, b) => b.spanCount - a.spanCount);
  return result;
}

export function groupSpansByTrace(spanList) {
  const map = {};
  for (const s of spanList) {
    if (!map[s.trace_id]) map[s.trace_id] = [];
    map[s.trace_id].push(s);
  }
  return Object.entries(map).map(([trace_id, traceSpans]) => {
    const root = traceSpans.find((s) => !s.parent_span_id);
    // Use reduce to avoid stack overflow with large arrays
    const start = traceSpans.reduce(
      (min, s) => (s.start_time_ns < min ? s.start_time_ns : min),
      Infinity,
    );
    const end = traceSpans.reduce(
      (max, s) => (s.end_time_ns > max ? s.end_time_ns : max),
      -Infinity,
    );
    return {
      trace_id,
      root_span_name: root?.name || "unknown",
      span_count: traceSpans.length,
      duration_ns: end - start,
      spans: traceSpans,
    };
  });
}

export function sortSpansForWaterfall(spanList) {
  const byId = {};
  for (const s of spanList) byId[s.span_id] = s;
  const visited = new Set();
  const result = [];

  function visit(span) {
    if (visited.has(span.span_id)) return;
    visited.add(span.span_id);
    result.push(span);
    const children = spanList
      .filter((s) => s.parent_span_id === span.span_id)
      .sort((a, b) => a.start_time_ns - b.start_time_ns);
    for (const child of children) visit(child);
  }

  const roots = spanList
    .filter((s) => !s.parent_span_id || !byId[s.parent_span_id])
    .sort((a, b) => a.start_time_ns - b.start_time_ns);
  for (const root of roots) visit(root);
  for (const s of spanList) {
    if (!visited.has(s.span_id)) result.push(s);
  }
  return result;
}

export function formatDur(ns) {
  const ms = ns / 1e6;
  if (ms < 0.001) return `${ns.toFixed(0)}ns`;
  if (ms < 1) return `${(ns / 1000).toFixed(0)}μs`;
  if (ms < 1000) return `${ms.toFixed(ms < 10 ? 1 : 0)}ms`;
  const s = ms / 1000;
  return s < 60 ? `${s.toFixed(s < 10 ? 1 : 0)}s` : `${(s / 60).toFixed(1)}m`;
}

export function buildTicks(startNs, durNs, baseNs, count = 8) {
  if (durNs <= 0) return [];
  const raw = durNs / count;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / mag;
  const step = (n < 1.5 ? 1 : n < 3.5 ? 2 : n < 7.5 ? 5 : 10) * mag;
  const first = Math.ceil(startNs / step) * step;
  const ticks = [];
  for (let ns = first; ns <= startNs + durNs; ns += step) {
    ticks.push({ pct: ((ns - startNs) / durNs) * 100, label: formatDur(ns - baseNs) });
  }
  return ticks;
}

export function barStyle(span, startNs, durNs, laneH, laneGap, rowPad) {
  const endNs = startNs + durNs;
  if (span.end_time_ns <= startNs || span.start_time_ns >= endNs) return null;
  const cs = Math.max(span.start_time_ns, startNs);
  const ce = Math.min(span.end_time_ns, endNs);
  const left = ((cs - startNs) / durNs) * 100;
  const width = Math.max(0.15, ((ce - cs) / durNs) * 100);
  const top = rowPad + span.lane * (laneH + laneGap);
  return `left:${left}%;width:${width}%;top:${top}px;height:${laneH}px;`;
}

export function rowHeight(laneCount, laneH, laneGap, rowPad) {
  return rowPad * 2 + laneCount * laneH + Math.max(0, laneCount - 1) * laneGap;
}

export function hue(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) & 0xffffffff;
  return Math.abs(h) % 360;
}

export function spanColor(span, colorBy) {
  const key = colorBy === "trace" ? span.trace_id : span.name;
  const h = hue(key);
  return {
    bg: `hsl(${h},65%,55%)`,
    border: `hsl(${h},65%,38%)`,
  };
}
